// =============================================================================================================================================================
// TenderConfigCrud.jsx
// =============================================================================================================================================================
// Single CRUD page for both Spot and Non-Spot tenders.
//
// I use this component in two very different hosting models:
//
//   1) Standard backoffice usage (non-carrier realm)
//      - View/Edit a Tender or a Tender Phase.
//      - The Save button is visible in Edit mode and uses the Update DTOs.
//
//   2) Carrier realm usage
//      - Here the Tender is read-only and expose a "Bid" button when the business rules allow it.
//      - Originally, this button opened a new tab pointing to CarrierSubmitPage,
//        passing the bid context through Redux M_State.
//
//      - support an embedded "wrapper" scenario (CarrierInviteViewBidPage):
//          * The wrapper renders TenderConfigCrud and CarrierSubmitPage in the same tab.
//          * In this mode it NOT open a new tab.
//          * Instead, when the user clicks "Bid" I call an optional callback: onOpenBid.
//          * That callback receives the bid context (scope, crudMode, tenderId, phaseId, DTOs, lanes, header…),
//            and the wrapper passes everything directly to CarrierSubmitPage via props.
//
//      - If onOpenBid is not provided, fallbacks to the legacy behaviour:
//          * write CarrierBidContext / CarrierBidTender into M_State.
//          * call onCreateTab("tenders/CarrierSubmitPage", label) to open a new tab.
//
// This means:
//
//   - Outside carrier realm: nothing changes.
//   - In carrier realm without wrapper: still opens a separate CarrierSubmitPage tab.
//   - In carrier realm with wrapper: I never open a new tab, I just notify the wrapper.
//
// Invite-aware usage
// ------------------
// When this component is hosted under CarrierInviteViewBidPage, it can receive an optional
// 'inviteContext' prop, which is derived from the deep-link URL:
//
//   inviteContext = {
//     scope: "tender" | "phase",
//     type:  "spot"   | "phase",
//     tenderId?: number,
//     tenderPhaseId?: number,
//     token?: string,
//     createdAt?: number
//   }
//
// To avoid breaking existing flows, this invite context is only used when there is no
// existing line/phase context coming from M_State (globalLine/globalPhaseLine).
// In that case, it simply seeds the initial line/phase used for `getRefresh`, leaving
// all other behaviors untouched.
// =============================================================================================================================================================

import React, {
    useState,
    useEffect,
    useContext,
    useRef,
    useMemo,
    useCallback,
} from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    LoadingContext,
    MHeader,
    MButton,
    BufferService,
    MCheckBox,
    M_StateActions,
    NavigateContext,
} from "src/M_Components";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";

// Dynamic Form to render Tender and Phase main forms
import DynamicForm from "../../components/Forms/DynamicForm";
// Packages (FCL | LCL) panel
import PackagesPanel from "../../components/InstantQuotesSearch/PackagesPanel";
// Unified tile selector (supports view and API-paginated modes)
import SelectableTileGrid from "../../components/SelectableTileGrid/SelectableTileGrid";

// =============================================================================================================================================================
// Helpers
// =============================================================================================================================================================
const norm = (data) =>
    Array.isArray(data?.Items) ? data.Items : Array.isArray(data) ? data : [];

const asArray = (v) => (Array.isArray(v) ? v : []);
const isSpot = (dto) =>
    Number(dto?.TenderTypeId ?? 1) === 1 ||
    String(dto?.TenderType || "").toLowerCase() === "spot";
const isNonSpot = (dto) => Number(dto?.TenderTypeId ?? 0) === 2;

// Small deep equality to avoid redundant state writes (used for grid state persistence)
const deepEq = (a, b) => {
    try {
        return JSON.stringify(a) === JSON.stringify(b);
    } catch {
        return a === b;
    }
};

// NOTE: Tender overrides only — I do NOT override CREATE here.
// Hide keys/audit + hide TenderTypeId both in View and Edit, per requirement.
const includeTenderInEdit = (opt) => {
    if (!opt) return false;
    if (opt.IsKey) return false;
    // Hide common audit fields if present in schema (keeps UI clean).
    if (["CreatedAt", "UpdatedAt", "CreatedBy", "UpdatedBy"].includes(opt.Name)) return false;
    if (opt.IsHiddenEdit === true) return false;
    if (opt.Name === "TenderType") return false; // requirement: hide here
    return true;
};

// =============================================================================================================================================================
// Component
// =============================================================================================================================================================
export default function TenderConfigCrud({
    handleTabClose,
    toastMessages,
    isCarrierRealm = false, // when true, page runs in carrier realm (view-only + Bid)
    onCreateTab,            // legacy tab opener (used when I still open CarrierSubmitPage as a new tab)
    roles,                  // accepted for compatibility with route, not used here yet

    // optional callback used by the embedded wrapper (CarrierInviteViewBidPage).
    // When this callback is provided, I do NOT open a new tab on Bid.
    // Instead I call onOpenBid(bidContext, bidHeader) and let the wrapper swap the view.
    onOpenBid,

    // INVITE: optional deep-link context for carrier invite flows.
    // Used only when there is no existing line/phase context from M_State.
    inviteContext = null,
    //{ scope: "tender"|"phase", tenderId: number|null, tenderPhaseId: number|null }
    configSeed = null,
}) {
    const { t, i18n } = useTranslation();
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const M_State = useSelector((s) => s.StateKeyValue);
    const selectedTab = useSelector((s) =>
        s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
    );
    const { renameTab } = useContext(NavigateContext);

    // ---------------------------------------------------------------------
    // One-shot globals from table handover
    // ---------------------------------------------------------------------
    const globalOperation = M_State.find((o) => o.key === "operation")?.value;
    const globalLine = M_State.find((o) => o.key === "TenderLine")?.value;

    // Phase mode globals (flag + selected phase line)
    const globalIsPhaseMode =
        M_State.find((o) => o.key === "IsPhaseMode")?.value ??
        M_State.find((o) => o.key === "PhaseMode")?.value ??
        false;
    const globalPhaseLine = M_State.find((o) => o.key === "TenderPhaseLine")?.value;

    // ---------------------------------------------------------------------
    // Per-tab id + page mode + selected line
    // ---------------------------------------------------------------------
    const [id, setId] = useState(
        M_State.find((o) => o.key === "selectedTab")?.value
    );
    const [operation, setOperation] = useState(globalOperation ?? "View");
    const [sessionLine, setSessionLine] = useState(globalLine ?? null);

    // Derived flags: carrier realm is always view-only
    const rawIsViewMode = String(operation || "").toLowerCase() === "view";
    const rawIsEditMode = String(operation || "").toLowerCase() === "edit";
    const isViewMode = isCarrierRealm ? true : rawIsViewMode;
    const isEditMode = isCarrierRealm ? false : rawIsEditMode;

    // ---------------------------------------------------------------------
    // Phase mode state
    // ---------------------------------------------------------------------
    const [isPhaseMode, setIsPhaseMode] = useState(!!globalIsPhaseMode);
    const [phaseLine, setPhaseLine] = useState(globalPhaseLine ?? null); // { Id, ... } expected

    // ---------------------------------------------------------------------
    // Services (memoized). Use explicit baseUrl to avoid hooks inside constructors.
    // ---------------------------------------------------------------------
    const baseUrl = ctx.DEFAULTURL;

    const tenderService = useMemo(
        () => new BufferService("Tenders", baseUrl),
        [baseUrl]
    );
    const tenderUpdateService = useMemo(
        () => new BufferService("Tenders/update", baseUrl),
        [baseUrl]
    );

    const tenderPhaseService = useMemo(
        () => new BufferService("TenderPhases", baseUrl),
        [baseUrl]
    );
    const tenderPhaseUpdateService = useMemo(
        () => new BufferService("TenderPhases/update", baseUrl),
        [baseUrl]
    );

    const tenderOptionsService = useMemo(
        () => new BufferService("Tenders", baseUrl),
        [baseUrl]
    );
    const tenderPhaseOptionsService = useMemo(
        () => new BufferService("TenderPhases", baseUrl),
        [baseUrl]
    );

    const equipmentService = useMemo(
        () => new BufferService("CargofiveEquipments", baseUrl),
        [baseUrl]
    );
    const packageTypesService = useMemo(
        () => new BufferService("PackageTypes", baseUrl),
        [baseUrl]
    );

    // ---------------------------------------------------------------------
    // Data state (Tender DTO + schema/options)
    // ---------------------------------------------------------------------
    const [tender, setTender] = useState({});
    const [phases, setPhases] = useState([]);
    const [carriers, setCarriers] = useState([]);
    const [transportTypes, setTransportTypes] = useState([]);
    const [markets, setMarkets] = useState([]);

    const [tenderSchemaOptions, setTenderSchemaOptions] = useState([]);
    const [phaseSchemaOptions, setPhaseSchemaOptions] = useState([]);

    const [tenderPhase, setTenderPhase] = useState({});
    const [lastPhase, setLastPhase] = useState(null);
    const [phaseCarriers, setPhaseCarriers] = useState([]);

    // ---------------------------------------------------------------------
    // Packages master + working state
    // ---------------------------------------------------------------------
    const [equipments, setEquipments] = useState([]); // FCL equipments
    const [packageTypes, setPackageTypes] = useState([]); // LCL package types

    const [updatePackagesEnabled, setUpdatePackagesEnabled] = useState(false);
    const containerTypeRef = useRef(null); // to detect ContainerTypeId changes

    // LCL state
    const [lclMode, setLclMode] = useState("volumes"); // 'volumes' | 'dimensions'
    const [lclBase, setLclBase] = useState("plt"); // 'plt' | 'box'
    const [lclVolumes, setLclVolumes] = useState(null); // { quantity, volumeM3, weightKg, packageTypeId }
    const [lclDimensions, setLclDimensions] = useState([]); // array of { quantity, widthCm, heightCm, lengthCm, weightKg, packageTypeId }

    // FCL items
    const [fclItems, setFclItems] = useState([]); // [{ cargofiveEquipmentId, quantity, weightKg }]

    // Loading flags
    const [baseLoaded, setBaseLoaded] = useState(false);
    const [tenderLoaded, setTenderLoaded] = useState(false);
    const [phaseLoaded, setPhaseLoaded] = useState(!isPhaseMode);

    // Imperative ref to the Tender DynamicForm
    const tenderFormRef = useRef(null);
    // Imperative ref to the Phase DynamicForm (only used in Phase mode)
    const phaseFormRef = useRef(null);

    // ------------------------------------------------------------------------------------------------
    // Initial global loading gate (first mount only)
    // ------------------------------------------------------------------------------------------------
    // This prevents the page from visually "building/growing" while dynamic children perform their own requests.
    // While the gate is not released, the global overlay is forced ON.
    // Once the page reaches its ready state for the first time, the gate is released and normal behavior resumes.
    const initialGateReleasedRef = useRef(false);
    const gateRafRef = useRef(null);

    // ---------------------------------------------------------------------
    // Per-tab grid UI states (persisted) + stable scope id for isolation
    // ---------------------------------------------------------------------
    const [mkGridState, setMkGridState] = useState(null);
    const [ttGridState, setTtGridState] = useState(null);
    const [crGridState, setCrGridState] = useState(null);

    const tabScopeId = useMemo(
        () => (id ? `tc-${String(id)}` : "tc-unknown"),
        [id]
    );

    // =============================================================================================================================================================
    // Tab label (mode + tender or phase name)
    // =============================================================================================================================================================
    useEffect(() => {
        if (!selectedTab || !operation) return;
        const base = isPhaseMode
            ? operation === "Edit"
                ? t("LabelEditTenderPhase") || "Edit Tender Phase"
                : t("LabelViewTenderPhase") || "View Tender Phase"
            : operation === "Edit"
                ? t("LabelEditTender")
                : operation === "View"
                    ? t("LabelViewTender")
                    : t("Tender");
        const name = isPhaseMode
            ? tenderPhase?.Name || phaseLine?.Name || tender?.Name || ""
            : tender?.Name || sessionLine?.Name || "";
        const label = name ? `${base} "${name}"` : base;
        renameTab(String(selectedTab), label);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedTab,
        operation,
        tender?.Name,
        sessionLine?.Name,
        isPhaseMode,
        tenderPhase?.Name,
        phaseLine?.Name,
        i18n.language,
    ]);

    // =============================================================================================================================================================
    // Boot: restore per-tab snapshot BEFORE fetching; then consume one-shot globals; then fetch base + details
    //  - Now also honors an explicit configSeed coming from the parent wrapper (CarrierInviteViewBidPage)
    //    so we always know which Tender / Phase to load when returning from CarrierSubmitPage.
    // =============================================================================================================================================================
    useEffect(() => {
        const snap = M_State.find((o) => o.key === id)?.value || [];
        const pick = (k) => snap.find((x) => x.key === k)?.value;

        // restore basic states
        const snapOp = pick("operation");
        const snapLine = pick("sessionLine");
        const snapIsPhase = pick("isPhaseMode");
        const snapPhaseLine = pick("phaseLine");

        const snapTender = pick("tender");
        const snapTenderPhase = pick("tenderPhase");
        const snapLastPhase = pick("lastPhase");
        const snapPhaseCarriers = pick("phaseCarriers");

        const snapPhases = pick("phases");
        const snapCarriers = pick("carriers");
        const snapTT = pick("transportTypes");
        const snapMk = pick("markets");

        const snapUpd = pick("updatePackagesEnabled");
        const snapLclMode = pick("lclMode");
        const snapLclBase = pick("lclBase");
        const snapLclVol = pick("lclVolumes");
        const snapLclDim = pick("lclDimensions");
        const snapFcl = pick("fclItems");

        //  restore grid UI states
        const snapGridMk = pick("gridMk");
        const snapGridTt = pick("gridTt");
        const snapGridCr = pick("gridCr");

        if (snapTender) setTender(snapTender);
        if (snapTenderPhase) setTenderPhase(snapTenderPhase);
        if (snapLastPhase) setLastPhase(snapLastPhase);
        if (snapPhaseCarriers) setPhaseCarriers(snapPhaseCarriers);
        if (snapPhases) setPhases(snapPhases);
        if (snapCarriers) setCarriers(snapCarriers);
        if (snapTT) setTransportTypes(snapTT);
        if (snapMk) setMarkets(snapMk);

        if (typeof snapUpd === "boolean") setUpdatePackagesEnabled(snapUpd);
        if (snapLclMode) setLclMode(snapLclMode);
        if (snapLclBase) setLclBase(snapLclBase);
        if (snapLclVol) setLclVolumes(snapLclVol);
        if (snapLclDim) setLclDimensions(snapLclDim);
        if (snapFcl) setFclItems(snapFcl);

        if (snapGridMk) setMkGridState(snapGridMk);
        if (snapGridTt) setTtGridState(snapGridTt);
        if (snapGridCr) setCrGridState(snapGridCr);

        // Prefer globals over snapshot when present
        let seedOperation =
            globalOperation !== undefined ? globalOperation : snapOp ?? "View";
        let seedLine =
            globalLine !== undefined ? globalLine : snapLine ?? null;
        let seedIsPhase =
            globalIsPhaseMode !== undefined
                ? !!globalIsPhaseMode
                : !!(snapIsPhase ?? false);
        let seedPhaseLine =
            globalPhaseLine !== undefined ? globalPhaseLine : snapPhaseLine ?? null;

        // ------------------------------------------------------------------------------------------------
        // If the parent wrapper provided an explicit seed (configSeed),
        //      prefer it over snapshot / globals.
        //
        // Isto é crítico no fluxo de convite do carrier:
        //  - CarrierInviteViewBidPage sabe exatamente o Tender/TenderPhase (via bidContext)
        //  - Ao voltar do CarrierSubmitPage (Back), usamos esse seed para
        //    garantir que o TenderConfigCrud carrega logo o alvo correto,
        //    mesmo depois de login e re-hidratação de tabs.
        // ------------------------------------------------------------------------------------------------
        if (configSeed && (configSeed.tenderId || configSeed.tenderPhaseId)) {
            const seedScope = (configSeed.scope || "").toLowerCase();
            const isPhaseSeed =
                seedScope === "phase" || !!configSeed.tenderPhaseId;

            seedIsPhase = isPhaseSeed;

            if (isPhaseSeed && configSeed.tenderPhaseId) {
                // Phase-based seed: lock phaseLine to the given phase id.
                seedPhaseLine = { Id: Number(configSeed.tenderPhaseId) };
                // Parent Tender will be inferred later from phase DTO;
                // no need to force seedLine here.
            } else if (!isPhaseSeed && configSeed.tenderId) {
                // Tender-based seed: lock main line to the given tender id.
                seedLine = { Id: Number(configSeed.tenderId) };
            }
        }

        // INVITE: If there is no explicit line/phase from M_State or snapshot (or configSeed),
        // and an inviteContext is present, derive the initial target from it.
        //
        // This is strictly opt-in:
        //   - It never overrides an existing globalLine/globalPhaseLine OR configSeed.
        //   - It only runs when both seedLine and seedPhaseLine are empty.
        if (!seedLine && !seedPhaseLine && inviteContext) {
            const inviteIsPhase =
                inviteContext.scope === "phase" ||
                inviteContext.type === "phase" ||
                !!inviteContext.tenderPhaseId;

            if (inviteIsPhase && inviteContext.tenderPhaseId) {
                seedIsPhase = true;
                seedPhaseLine = { Id: inviteContext.tenderPhaseId };
                // parent Tender will be inferred later from phase DTO;
                // no need to seed seedLine here.
            } else if (!inviteIsPhase && inviteContext.tenderId) {
                seedIsPhase = false;
                seedLine = { Id: inviteContext.tenderId };
            }
        }

        // In carrier realm I always enforce View operation (UI-wise)
        const resolvedOperation = isCarrierRealm ? "View" : seedOperation;

        setOperation(resolvedOperation);
        setIsPhaseMode(!!seedIsPhase);
        if (seedLine) setSessionLine(seedLine);
        if (seedPhaseLine) setPhaseLine(seedPhaseLine);

        // Clear one-shot globals to preserve tab isolation
        if (globalOperation !== undefined)
            dispatch(M_StateActions.removeM_State({ key: "operation" }));
        if (globalLine !== undefined)
            dispatch(M_StateActions.removeM_State({ key: "TenderLine" }));
        if (globalIsPhaseMode !== undefined)
            dispatch(M_StateActions.removeM_State({ key: "IsPhaseMode" }));
        if (globalPhaseLine !== undefined)
            dispatch(M_StateActions.removeM_State({ key: "TenderPhaseLine" }));

        // Fetch chain (Tender + Phase) using the resolved seeds.
        // NOTE: at this point, seedLine/seedPhaseLine may come from:
        //   - M_State snapshot
        //   - one-shot globals
        //   - explicit configSeed (preferred in carrier invite flow)
        //   - inviteContext (last fallback)
        getRefresh(seedLine, resolvedOperation, seedIsPhase, seedPhaseLine);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteContext, configSeed]);



    // =============================================================================================================================================================
    // Persist snapshot per tab
    // =============================================================================================================================================================
    useEffect(() => {
        if (!id) return;

        const currentTenderValues =
            tenderFormRef.current?.getValues?.() &&
                isEditMode &&
                !isPhaseMode
                ? tenderFormRef.current.getValues()
                : {};
        const tenderToPersist =
            isEditMode && !isPhaseMode
                ? { ...(tender || {}), ...currentTenderValues }
                : tender;

        const entries = [
            { key: "operation", value: operation },
            { key: "sessionLine", value: sessionLine },
            { key: "isPhaseMode", value: isPhaseMode },
            { key: "phaseLine", value: phaseLine },
            { key: "tender", value: tenderToPersist },
            { key: "tenderPhase", value: tenderPhase },
            { key: "lastPhase", value: lastPhase },
            { key: "phaseCarriers", value: phaseCarriers },
            { key: "phases", value: phases },
            { key: "carriers", value: carriers },
            { key: "transportTypes", value: transportTypes },
            { key: "markets", value: markets },
            { key: "gridMk", value: mkGridState },
            { key: "gridTt", value: ttGridState },
            { key: "gridCr", value: crGridState },
            { key: "updatePackagesEnabled", value: updatePackagesEnabled },
            { key: "lclMode", value: lclMode },
            { key: "lclBase", value: lclBase },
            { key: "lclVolumes", value: lclVolumes },
            { key: "lclDimensions", value: lclDimensions },
            { key: "fclItems", value: fclItems },
        ];

        dispatch(M_StateActions.addM_State({ key: id, value: entries }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        id,
        operation,
        sessionLine,
        isPhaseMode,
        phaseLine,
        tender,
        tenderPhase,
        lastPhase,
        phaseCarriers,
        phases,
        carriers,
        transportTypes,
        markets,
        mkGridState,
        ttGridState,
        crGridState,
        updatePackagesEnabled,
        lclMode,
        lclBase,
        lclVolumes,
        lclDimensions,
        fclItems,
        isEditMode,
    ]);

    // =============================================================================================================================================================
    // Track tab change and restore snapshot for the new tab id
    // =============================================================================================================================================================
    useEffect(() => {
        const newId = M_State.find((o) => o.key === "selectedTab")?.value;
        if (newId && newId !== id) {
            setId(newId);
            restoreFor(newId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [M_State]);

    const restoreFor = (sessionID) => {
        ctx.setIsLoad(true);
        const data = M_State.find((o) => o.key === sessionID)?.value;
        if (data) {
            const pick = (k) => data.find((x) => x.key === k)?.value;

            const op = pick("operation") ?? "View";
            const resolvedOp = isCarrierRealm ? "View" : op;

            setOperation(resolvedOp);
            setSessionLine(pick("sessionLine") ?? null);
            setIsPhaseMode(!!(pick("isPhaseMode") ?? false));
            setPhaseLine(pick("phaseLine") ?? null);

            setTender(pick("tender") ?? {});
            setTenderPhase(pick("tenderPhase") ?? {});
            setLastPhase(pick("lastPhase") ?? null);
            setPhaseCarriers(pick("phaseCarriers") ?? []);

            setPhases(pick("phases") ?? []);
            setCarriers(pick("carriers") ?? []);
            setTransportTypes(pick("transportTypes") ?? []);
            setMarkets(pick("markets") ?? []);

            setMkGridState(pick("gridMk") ?? null);
            setTtGridState(pick("gridTt") ?? null);
            setCrGridState(pick("gridCr") ?? null);

            setUpdatePackagesEnabled(pick("updatePackagesEnabled") ?? false);
            setLclMode(pick("lclMode") ?? "volumes");
            setLclBase(pick("lclBase") ?? "plt");
            setLclVolumes(pick("lclVolumes") ?? null);
            setLclDimensions(pick("lclDimensions") ?? []);
            setFclItems(pick("fclItems") ?? []);

            if (initialGateReleasedRef.current) {
                ctx.setIsLoad(false);
            }
            return;
        }
        getRefresh(sessionLine, operation, isPhaseMode, phaseLine);
    };

    // ===========================================================================================================================================================
    // Refresh chain (base lists + schemas + details).
    // ===========================================================================================================================================================
    const getRefresh = async (
        line,
        op = operation,
        phaseMode = isPhaseMode,
        pLine = phaseLine
    ) => {
        ctx.setIsLoad(true);

        // ------------------------------------------------------------------------------------
        // IMPORTANT:
        // Robust invite fallback (carrier realm):
        // If there is no explicit line/phase yet but we do have an inviteContext,
        // derive the effective target from it and persist into local state.
        //
        // This ensures that, even when the flow passed through login and tab
        // re-hydration, TenderConfigCrud can still resolve the correct Tender or
        // TenderPhase when entering from CarrierInviteViewBidPage.
        // ------------------------------------------------------------------------------------
        let effectivePhaseMode = phaseMode;
        let effectiveLine = line;
        let effectivePhaseLine = pLine;

        if (isCarrierRealm && inviteContext && !effectiveLine && !effectivePhaseLine) {
            const inviteIsPhase =
                inviteContext.scope === "phase" ||
                inviteContext.type === "phase" ||
                !!inviteContext.tenderPhaseId;

            effectivePhaseMode = inviteIsPhase;

            if (inviteIsPhase && inviteContext.tenderPhaseId) {
                effectivePhaseLine = { Id: inviteContext.tenderPhaseId };
            } else if (!inviteIsPhase && inviteContext.tenderId) {
                effectiveLine = { Id: inviteContext.tenderId };
            }

            // Persist resolved state so the rest of the component can rely on it.
            setIsPhaseMode(!!inviteIsPhase);
            if (effectiveLine) setSessionLine(effectiveLine);
            if (effectivePhaseLine) setPhaseLine(effectivePhaseLine);
        }

        let pkgTypesLoaded = [];

        try {
            const [equipRes, pkgTypesRes, tenderOpts, phaseOpts] =
                await Promise.all([
                    equipmentService.get(),
                    packageTypesService.get(),
                    tenderOptionsService.options?.(),
                    tenderPhaseOptionsService.options?.(),
                ]);

            if (!isFailurePayload(equipRes)) setEquipments(norm(equipRes) || []);
            else toastMessages("error", "", apiMessage(equipRes, t("ErrorLoadingData")));

            if (!isFailurePayload(pkgTypesRes)) {
                pkgTypesLoaded = norm(pkgTypesRes) || [];
                setPackageTypes(pkgTypesLoaded);
            } else {
                toastMessages(
                    "error",
                    "",
                    apiMessage(pkgTypesRes, t("ErrorLoadingData"))
                );
            }

            if (!isFailurePayload(tenderOpts)) {
                const schema = Array.isArray(tenderOpts) ? tenderOpts : [];
                setTenderSchemaOptions(schema);
            } else {
                setTenderSchemaOptions([]);
            }

            if (!isFailurePayload(phaseOpts)) {
                const phaseSchema = Array.isArray(phaseOpts) ? phaseOpts : [];
                setPhaseSchemaOptions(phaseSchema);
            } else {
                setPhaseSchemaOptions([]);
            }

            setBaseLoaded(true);
        } catch (err) {
            setBaseLoaded(true);
            toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
        }

        try {
            if (effectivePhaseMode) {
                const phaseId = effectivePhaseLine?.Id;
                if (!phaseId) throw new Error("Missing TenderPhase id for phase mode.");
                const phaseDto = await tenderPhaseService.getId(phaseId);
                if (isFailurePayload(phaseDto)) {
                    toastMessages("error", "", apiMessage(phaseDto, t("ErrorLoadingData")));
                } else {
                    setTenderPhase(phaseDto || {});
                    setPhaseCarriers(
                        asArray(phaseDto?.Carriers).map((c) => ({
                            Id: c?.Id,
                            Name: c?.Name || c?.Code || "",
                        }))
                    );
                    if (phaseDto?.LastTenderPhaseId) {
                        try {
                            const lastDto = await tenderPhaseService.getId(
                                phaseDto.LastTenderPhaseId
                            );
                            if (!isFailurePayload(lastDto)) {
                                setLastPhase(lastDto);
                            } else {
                                setLastPhase(null);
                            }
                        } catch {
                            setLastPhase(null);
                        }
                    } else {
                        setLastPhase(null);
                    }

                    const parentTenderId = phaseDto?.TenderId;
                    if (parentTenderId) {
                        const dto = await tenderService.getId(parentTenderId);
                        if (isFailurePayload(dto)) {
                            toastMessages(
                                "error",
                                "",
                                apiMessage(dto, t("ErrorLoadingData"))
                            );
                        } else {
                            setTender(dto || {});
                            setPhases(asArray(dto?.Phases));
                            setTransportTypes(
                                asArray(dto?.TransportTypes).map((tt) => ({
                                    Id: tt?.TransportType?.Id ?? tt?.TransportTypeId ?? tt?.Id,
                                    Name: tt?.TransportType?.Name ?? "",
                                }))
                            );
                            setMarkets(
                                asArray(dto?.Markets).map((m) => ({
                                    Id: m?.Market?.Id ?? m?.MarketId ?? m?.Id,
                                    Name: m?.Market?.Name ?? "",
                                }))
                            );
                            setCarriers([]);

                            initPackagesStateFromDTO(dto, []);
                            containerTypeRef.current = Number(
                                dto?.ContainerTypeId ?? 1
                            );
                            setTenderLoaded(true);
                            setPhaseLoaded(true);
                        }
                    } else {
                        setTender({});
                        setPhases([]);
                        setTransportTypes([]);
                        setMarkets([]);
                        setCarriers([]);
                        setTenderLoaded(true);
                        setPhaseLoaded(true);
                    }
                }
            } else {
                const effectiveLineForGet = effectiveLine || sessionLine;
                const effectiveId = effectiveLineForGet?.Id;

                if (effectiveId) {
                    const dto = await tenderService.getId(effectiveId);
                    if (isFailurePayload(dto)) {
                        toastMessages(
                            "error",
                            "",
                            apiMessage(dto, t("ErrorLoadingData"))
                        );
                    } else {
                        setTender(dto || {});
                        setPhases(asArray(dto?.Phases));
                        setCarriers(
                            asArray(dto?.Carriers).map((c) => ({
                                Id: c?.Carrier?.Id ?? c?.CarrierId ?? c?.Id,
                                Name: c?.Carrier?.Name ?? "",
                            }))
                        );
                        setTransportTypes(
                            asArray(dto?.TransportTypes).map((tt) => ({
                                Id: tt?.TransportType?.Id ?? tt?.TransportTypeId ?? tt?.Id,
                                Name: tt?.TransportType?.Name ?? "",
                            }))
                        );
                        setMarkets(
                            asArray(dto?.Markets).map((m) => ({
                                Id: m?.Market?.Id ?? m?.MarketId ?? m?.Id,
                                Name: m?.Market?.Name ?? "",
                            }))
                        );

                        initPackagesStateFromDTO(dto, pkgTypesLoaded);

                        containerTypeRef.current = Number(
                            dto?.ContainerTypeId ?? 1
                        );
                        setTenderLoaded(true);
                        setPhaseLoaded(true);
                    }
                } else {
                    setTenderLoaded(true);
                    setPhaseLoaded(true);
                }
            }
        } catch (err) {
            setTenderLoaded(true);
            setPhaseLoaded(true);
            toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
        } finally {
            if (initialGateReleasedRef.current) {
                ctx.setIsLoad(false);
            }
        }
    };

    // =============================================================================================================================================================
    // Initialize packages working state from DTO (view baseline)
    // =============================================================================================================================================================
    const initPackagesStateFromDTO = (dto, pkgTypesParam) => {
        const pkgTypes = Array.isArray(pkgTypesParam)
            ? pkgTypesParam
            : packageTypes;
        const ctype = Number(dto?.ContainerTypeId ?? 1);
        if (ctype === 2) {
            const fcl = asArray(dto?.FclPackages).map((p) => ({
                cargofiveEquipmentId: p?.CargofiveEquipmentId ?? null,
                quantity: p?.Quantity ?? 0,
                weightKg: p?.WeightKg ?? 0,
            }));
            setFclItems(fcl);
            setLclMode("volumes");
            setLclBase("plt");
            setLclVolumes(null);
            setLclDimensions([]);
        } else {
            const lcl = dto?.LclPackage || null;
            const hasVol = !!lcl?.VolumePackage;
            setLclMode(hasVol ? "volumes" : "dimensions");
            const pkgTypeIdVol = Number(lcl?.VolumePackage?.PackageTypeId ?? 0);
            const pkgTypeIdDim = Number(
                lcl?.DimensionsPackage?.PackageTypeId ?? 0
            );
            const baseFromDto = (id) => {
                const match = Array.isArray(pkgTypes)
                    ? pkgTypes.find((p) => Number(p.Id) === Number(id))
                    : null;
                const text = (match?.Code ?? match?.Name ?? "").toLowerCase();
                if (text.includes("box")) return "box";
                if (text.includes("pallet") || text.includes("plt")) return "plt";
                if (Number(id) === 2) return "box";
                if (Number(id) === 1) return "plt";
                return "plt";
            };
            if (hasVol) {
                setLclVolumes({
                    quantity: lcl?.VolumePackage?.Quantity ?? 0,
                    volumeM3: lcl?.VolumePackage?.VolumeM3 ?? 0,
                    weightKg: lcl?.VolumePackage?.WeightKg ?? 0,
                    packageTypeId: lcl?.VolumePackage?.PackageTypeId ?? 1,
                });
                setLclDimensions([]);
                setLclBase(baseFromDto(pkgTypeIdVol));
            } else if (lcl?.DimensionsPackage) {
                setLclVolumes(null);
                setLclDimensions([
                    {
                        quantity: lcl?.DimensionsPackage?.Quantity ?? 0,
                        wCm: lcl?.DimensionsPackage?.WidthCm ?? 0,
                        hCm: lcl?.DimensionsPackage?.HeightCm ?? 0,
                        lCm: lcl?.DimensionsPackage?.LengthCm ?? 0,
                        weightKg: lcl?.DimensionsPackage?.WeightKg ?? 0,
                        packageTypeId: lcl?.DimensionsPackage?.PackageTypeId ?? 1,
                    },
                ]);
                setLclBase(baseFromDto(pkgTypeIdDim));
            } else {
                setLclVolumes({
                    quantity: 0,
                    volumeM3: 0,
                    weightKg: 0,
                    packageTypeId: 1,
                });
                setLclDimensions([]);
                setLclBase("plt");
            }
            setFclItems([]);
        }
    };

    // =============================================================================================================================================================
    // Patch handlers (Lane-style onValuesChange, persisted per-tab)
    // =============================================================================================================================================================
    const handleTenderValuesChange = useCallback(
        (patch) => {
            if (!patch || !id) return;

            const prevTabState = M_State.find((o) => o.key === id)?.value || [];
            const pick = (k) =>
                prevTabState.find((x) => x.key === k)?.value;

            const next = { ...(pick("tender") || tender || {}), ...patch };
            setTender(next);

            if (
                !isPhaseMode &&
                isEditMode &&
                Object.prototype.hasOwnProperty.call(patch, "ContainerTypeId")
            ) {
                const prev = Number(
                    containerTypeRef.current ?? (tender?.ContainerTypeId ?? 1)
                );
                const now = Number(
                    patch.ContainerTypeId ?? next.ContainerTypeId ?? 1
                );
                if (now !== prev) {
                    if (!updatePackagesEnabled) setUpdatePackagesEnabled(true);
                    containerTypeRef.current = now;
                    initPackagesStateFromDTO(
                        { ...next, ContainerTypeId: now },
                        packageTypes
                    );
                }
            }

            const updated = [
                ...prevTabState.filter((x) => x.key !== "tender"),
                { key: "tender", value: next },
            ];
            dispatch(M_StateActions.addM_State({ key: id, value: updated }));
        },
        [
            M_State,
            id,
            tender,
            dispatch,
            isPhaseMode,
            isEditMode,
            updatePackagesEnabled,
            packageTypes,
        ]
    );

    const handlePhaseValuesChange = useCallback(
        (patch) => {
            if (!patch || !id) return;

            const prevTabState = M_State.find((o) => o.key === id)?.value || [];
            const pick = (k) =>
                prevTabState.find((x) => x.key === k)?.value;

            const next = {
                ...(pick("tenderPhase") || tenderPhase || {}),
                ...patch,
            };
            setTenderPhase(next);

            const updated = [
                ...prevTabState.filter((x) => x.key !== "tenderPhase"),
                { key: "tenderPhase", value: next },
            ];
            dispatch(M_StateActions.addM_State({ key: id, value: updated }));
        },
        [M_State, id, tenderPhase, dispatch]
    );

    // ===========================================================================================================================================================
    // Save (Update DTO)
    // ===========================================================================================================================================================
    const onSave = () => {
        ctx.setIsLoad(true);

        if (isPhaseMode) {
            const phaseId = tenderPhase?.Id ?? phaseLine?.Id;
            if (!phaseId) {
                toastMessages("error", "", t("ErrorSaving"));
                ctx.setIsLoad(false);
                return;
            }
            const payload = buildPhaseUpdatePayload();
            tenderPhaseUpdateService
                .update(phaseId, payload)
                .then((res) => {
                    if (isFailurePayload(res)) {
                        toastMessages(
                            "error",
                            "",
                            apiMessage(res, t("ErrorSaving"))
                        );
                        return;
                    }
                    toastMessages(
                        "success",
                        "",
                        t("TenderPhaseUpdated") || "Tender Phase updated"
                    );
                    handleTabClose(id);
                })
                .catch((err) =>
                    toastMessages(
                        "error",
                        "",
                        apiMessage(err, t("ErrorSaving"))
                    )
                )
                .finally(() => ctx.setIsLoad(false));
            return;
        }

        const tenderId = sessionLine?.Id;
        if (!tenderId) {
            toastMessages("error", "", t("ErrorSaving"));
            ctx.setIsLoad(false);
            return;
        }

        const payload = buildUpdatePayload();
        tenderUpdateService
            .update(tenderId, payload)
            .then((res) => {
                if (isFailurePayload(res)) {
                    toastMessages(
                        "error",
                        "",
                        apiMessage(res, t("ErrorSaving"))
                    );
                    return;
                }
                toastMessages("success", "", t("TenderUpdated"));
                handleTabClose(id);
            })
            .catch((err) =>
                toastMessages(
                    "error",
                    "",
                    apiMessage(err, t("ErrorSaving"))
                )
            )
            .finally(() => ctx.setIsLoad(false));
    };

    // =============================================================================================================================================================
    // Build Tender update payload
    // =============================================================================================================================================================
    const buildUpdatePayload = () => {
        const ctype = Number(tender?.ContainerTypeId ?? 1);
        const isFcl = ctype === 2;

        const getPackageTypeIdForBase = (base) => {
            const txt = (p) => (p?.Code ?? p?.Name ?? "").toLowerCase();
            if (base === "box") {
                const hit = packageTypes.find((p) => txt(p).includes("box"));
                return hit?.Id != null ? Number(hit.Id) : 2;
            }
            const hit = packageTypes.find((p) => {
                const s = txt(p);
                return s.includes("pallet") || s.includes("plt");
            });
            return hit?.Id != null ? Number(hit.Id) : 1;
        };

        const dto = {
            name: tender?.Name || "",
            shipmentStartDate: tender?.ShipmentStartDate || null,
            shipmentDeadline: tender?.ShipmentDeadline || null,
            responseDeadline: tender?.ResponseDeadline || null,
            pickupAddress: tender?.PickupAddress || "",
            deliveryAddress: tender?.DeliveryAddress || "",
            deliverTo: tender?.DeliverTo || "",
            message: tender?.Message || "",
            isFinished: !!tender?.IsFinished,
            inactive: !!tender?.Inactive,
            isUpdatingPackages: !!updatePackagesEnabled,
            containerTypeId: ctype,
        };

        if (updatePackagesEnabled) {
            if (isFcl) {
                dto.fclPackages = asArray(fclItems).map((p) => ({
                    tenderId: tender?.Id ?? 0,
                    cargofiveEquipmentId: Number(
                        p?.cargofiveEquipmentId ?? 0
                    ),
                    quantity: Number(p?.quantity ?? 0),
                    weightKg: Number(p?.weightKg ?? 0),
                }));
            } else {
                const vol = lclMode === "volumes";
                const packageTypeId = getPackageTypeIdForBase(lclBase);
                const dim0 = lclDimensions?.[0] || {};

                dto.lclPackage = {
                    tenderId: tender?.Id ?? 0,
                    volumePackage: vol
                        ? {
                            packageTypeId,
                            quantity: Number(
                                lclVolumes?.quantity ?? 0
                            ),
                            volumeM3: Number(
                                lclVolumes?.volumeM3 ?? 0
                            ),
                            weightKg: Number(
                                lclVolumes?.weightKg ?? 0
                            ),
                        }
                        : null,
                    dimensionsPackage: !vol
                        ? {
                            packageTypeId,
                            quantity: Number(dim0?.quantity ?? 0),
                            widthCm: Number(dim0?.wCm ?? 0),
                            heightCm: Number(dim0?.hCm ?? 0),
                            lengthCm: Number(dim0?.lCm ?? 0),
                            weightKg: Number(dim0?.weightKg ?? 0),
                        }
                        : null,
                };
            }
        }

        return dto;
    };

    // =============================================================================================================================================================
    // Build TenderPhase update payload
    // =============================================================================================================================================================
    const buildPhaseUpdatePayload = () => {
        const p = tenderPhase || {};
        return {
            name: p?.Name || "",
            startDate: p?.StartDate || null,
            endDate: p?.EndDate || null,
            inactive: !!p?.Inactive,
            isFinished: !!p?.IsFinished,
        };
    };

    // ===========================================================================================================================================================
    // Render helpers (view-only grids)
    // ===========================================================================================================================================================
    const MarketsView = () => (
        <div className="divFormCreateTender">
            <SelectableTileGrid
                key={`mk-${tabScopeId}`}
                pageSize={20}
                stateScopeId={`${tabScopeId}::grid::markets`}
                initialState={mkGridState || undefined}
                initialOrderDir={
                    mkGridState?.orderDir === "desc" ? "desc" : "asc"
                }
                onStateChange={(st) => {
                    setMkGridState((prev) => (deepEq(prev, st) ? prev : st));
                }}
                title={t("Markets")}
                viewMode
                items={markets}
                idField="Id"
                displayField="Name"
                queryable={false}
                orderable={false}
                staticFilter="Inactive eq false"
                selectedIds={(markets || []).map((m) => m.Id)}
                minHeight={"300px"}
                maxHeight={"300px"}
            />
        </div>
    );

    const TransportTypesView = () => (
        <div className="divFormCreateTender">
            <SelectableTileGrid
                key={`tt-${tabScopeId}`}
                pageSize={20}
                stateScopeId={`${tabScopeId}::grid::transportTypes`}
                initialState={ttGridState || undefined}
                initialOrderDir={
                    ttGridState?.orderDir === "desc" ? "desc" : "asc"
                }
                onStateChange={(st) => {
                    setTtGridState((prev) => (deepEq(prev, st) ? prev : st));
                }}
                title={t("TransportTypes")}
                viewMode
                items={transportTypes}
                idField="Id"
                displayField="Name"
                queryable={false}
                orderable={false}
                staticFilter="Inactive eq false"
                selectedIds={(transportTypes || []).map((tt) => tt.Id)}
                minHeight={"300px"}
                maxHeight={"300px"}
            />
        </div>
    );

    const CarriersListView = ({ list, title }) =>
        Array.isArray(list) && list.length > 0 ? (
            <div className="divFormCreateTender">
                <SelectableTileGrid
                    key={`cr-${tabScopeId}`}
                    pageSize={20}
                    stateScopeId={`${tabScopeId}::grid::carriers`}
                    initialState={crGridState || undefined}
                    initialOrderDir={
                        crGridState?.orderDir === "desc" ? "desc" : "asc"
                    }
                    onStateChange={(st) => {
                        setCrGridState((prev) =>
                            deepEq(prev, st) ? prev : st
                        );
                    }}
                    title={title}
                    viewMode
                    items={list}
                    idField="Id"
                    displayField="Name"
                    queryable={false}
                    orderable={false}
                    staticFilter="Inactive eq false"
                    selectedIds={(list || []).map((c) => c.Id)}
                    minHeight={"300px"}
                    maxHeight={"300px"}
                />
            </div>
        ) : null;

    const PhasesView = () =>
        !isPhaseMode && isNonSpot(tender) && phases.length > 0 ? (
            <div style={{ padding: "20px" }} className="divFormCreateTender">
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 20,
                    }}
                >
                    {phases.map((p) => (
                        <div key={p.Id}>
                            <DynamicForm
                                mode="view"
                                schema={phaseSchemaOptions}
                                data={p}
                                containerClassName="alignInputsUserForm"
                                title={t("TenderPhases")}
                            />
                        </div>
                    ))}
                </div>
            </div>
        ) : null;

    const PhasePanels = () =>
        isPhaseMode ? (
            <div style={{ padding: 20 }} className="divFormCreateTender">
                {lastPhase && (
                    <DynamicForm
                        mode="view"
                        schema={phaseSchemaOptions}
                        data={lastPhase}
                        containerClassName="alignInputsUserForm"
                        title="LastTenderPhase"
                    />
                )}

                <DynamicForm
                    key={`df-phase-${id}-${operation}-${(tenderPhase?.Id ?? phaseLine?.Id) ?? "na"
                        }`}
                    formRef={phaseFormRef}
                    mode={isEditMode ? "edit" : "view"}
                    schema={phaseSchemaOptions}
                    data={tenderPhase}
                    onValuesChange={handlePhaseValuesChange}
                    containerClassName="alignInputsUserForm"
                    title="TenderPhase"
                />
            </div>
        ) : null;

    // ===========================================================================================================================================================
    // Derived UI flags
    // ===========================================================================================================================================================
    const ready = baseLoaded && tenderLoaded && phaseLoaded;
    const containerTypeId = Number(tender?.ContainerTypeId ?? 1); // 1=LCL, 2=FCL
    const packagesReadOnly = isPhaseMode || !isEditMode || !updatePackagesEnabled;

    const isSpotTender = isSpot(tender);
    const isNonSpotTender = isNonSpot(tender);

    // -------------------------------------------------------------------------------------------------
    // Initial global loading gate control
    // -------------------------------------------------------------------------------------------------
    useEffect(() => {
        const tick = () => {
            if (!initialGateReleasedRef.current) {
                ctx.setIsLoad?.(true);
                gateRafRef.current = requestAnimationFrame(tick);
            }
        };

        tick();

        return () => {
            if (gateRafRef.current) cancelAnimationFrame(gateRafRef.current);
            gateRafRef.current = null;

            if (!initialGateReleasedRef.current) {
                ctx.setIsLoad?.(false);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (initialGateReleasedRef.current) return;

        if (ready) {
            initialGateReleasedRef.current = true;

            if (gateRafRef.current) cancelAnimationFrame(gateRafRef.current);
            gateRafRef.current = null;

            ctx.setIsLoad?.(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready]);

    /**
     * Rules to show/hide the Bid button in the Carrier realm:
     * - In Carrier realm:
     *     - Spot: always show to bid at Tender level.
     *     - Non-Spot: only bid to phases -> show only when isPhaseMode === true.
     */
    const shouldShowBidButton =
        isCarrierRealm &&
        (isSpotTender || (isNonSpotTender && isPhaseMode));

    // ===========================================================================================================================================================
    // UI handlers
    // ===========================================================================================================================================================
    const onRefresh = () => {
        getRefresh(sessionLine, operation, isPhaseMode, phaseLine);
    };

    // Carrier realm: trigger Bid flow either to the wrapper (embedded)
    // or to a new tab (legacy).
    const onBid = () => {
        // 1) Resolve the current Tender id
        const tenderId = tender?.Id ?? sessionLine?.Id;
        if (!tenderId) {
            toastMessages &&
                toastMessages("error", "", t("SelectedLine"));
            return;
        }

        // 2) Recover the CarrierBidOrigin written by the Invite page (if any)
        //    This still determines the desired scope and crudMode.
        const origin =
            M_State.find((o) => o.key === "CarrierBidOrigin")?.value || {};
        const scope = origin.scope || (isPhaseMode ? "phase" : "tender");
        const crudMode = origin.crudMode || "create"; // "create" | "edit" | "view"

        // 3) Resolve TenderPhaseId when in phase scope
        const tenderPhaseId =
            scope === "phase"
                ? tenderPhase?.Id ?? phaseLine?.Id ?? null
                : null;

        // 4) Build CarrierBidContext for the bid flow
        const bidContext = {
            scope,                // "tender" or "phase"
            crudMode,             // "create" | "edit" | "view"
            tenderId,
            tenderPhaseId,
            tenderDto: tender || null,
            tenderPhaseDto: scope === "phase" ? tenderPhase || null : null,
            lanes: tender?.Lanes || tenderPhase?.Lanes || [],
        };

        // 5) Minimal header payload (used only for UI)
        const bidLine = {
            Id: tenderId,
            Name: tender?.Name || sessionLine?.Name || "",
        };

        // 6) Embedded wrapper path:
        //    If onOpenBid is provided, I do not touch M_State for this context
        //    and I do not open a new tab. I simply raise the bid request upwards.
        if (typeof onOpenBid === "function") {
            onOpenBid(bidContext, bidLine);
            return;
        }

        // 7) Legacy path: write context into M_State and open CarrierSubmitPage as a new tab.
        //    If there is no tab navigation available, I stop here.
        if (!onCreateTab) {
            return;
        }

        dispatch(
            M_StateActions.addM_State({
                key: "CarrierBidContext",
                value: bidContext,
            })
        );

        dispatch(
            M_StateActions.addM_State({
                key: "CarrierBidTender",
                value: bidLine,
            })
        );

        const baseLabel = t("CarrierBid") || t("Bid") || "Bid";
        const extra = bidLine.Name || "";
        const tabLabel = extra ? `${baseLabel} "${extra}"` : baseLabel;

        onCreateTab("tenders/CarrierSubmitPage", tabLabel);
    };

    // ===========================================================================================================================================================
    // Render
    // ===========================================================================================================================================================
    return (
        <>
            <MHeader
                element={
                    <>
                        <MButton
                            tooltip={t("RefreshTable")}
                            icon={
                                <i className="pi pi-refresh iconTable"></i>
                            }
                            onClick={onRefresh}
                            label={t("RefreshTable")}
                        />
                        {/* Carrier realm: I only show Bid when business rules allow. Outside carrier realm, I keep Save. */}
                        {isCarrierRealm ? (
                            shouldShowBidButton && (
                                <MButton
                                    tooltip={t("Bid")}
                                    icon={
                                        <i className="pi pi-check-circle iconTable"></i>
                                    }
                                    onClick={onBid}
                                    label={t("Bid")}
                                    labelColor={"var(--color-green)"}
                                />
                            )
                        ) : (
                            isEditMode && (
                                <MButton
                                    tooltip={t("Save")}
                                    icon={
                                        <i className="pi pi-save iconTable"></i>
                                    }
                                    onClick={onSave}
                                    label={t("Save")}
                                />
                            )
                        )}
                    </>
                }
            />

            <div
                style={{
                    marginBottom: "10px",
                    padding: "20px",
                    background: "var(--color-white)",
                }}
            >
                <div className="firstDivContainer">
                    {/* Tender main info (always view in Phase mode; view or edit in Tender mode) */}
                    {ready && (
                        <div
                            className="divFormCreateTender"
                            style={{ padding: "20px" }}
                        >
                            <DynamicForm
                                key={`df-tender-${id}-${operation}-${sessionLine?.Id ?? "new"
                                    }-${isPhaseMode ? "phase" : "tender"}`}
                                formRef={tenderFormRef}
                                mode={
                                    isPhaseMode
                                        ? "view"
                                        : isViewMode
                                            ? "view"
                                            : "edit"
                                }
                                schema={tenderSchemaOptions}
                                data={tender}
                                onValuesChange={handleTenderValuesChange}
                                includeInEdit={
                                    !isPhaseMode ? includeTenderInEdit : undefined
                                }
                                containerClassName="alignInputsUserForm"
                                title={t("Tender Information")}
                            />
                        </div>
                    )}

                    {/* Phases */}
                    {!isPhaseMode && <PhasesView />}
                    {isPhaseMode && <PhasePanels />}

                    {/* Markets + TransportTypes */}
                    <div className="divAlignShipperMarkets">
                        <MarketsView />
                        <TransportTypesView />
                    </div>

                    {/* Carriers (view-only). Hidden in carrier realm. */}
                    {!isCarrierRealm &&
                        (isPhaseMode ? (
                            <CarriersListView
                                list={phaseCarriers}
                                title={t("Invited Carriers")}
                            />
                        ) : (
                            <CarriersListView
                                list={carriers}
                                title={t("Invited Carriers")}
                            />
                        ))}

                    {/* Update Packages toggle */}
                    {!isPhaseMode && isEditMode && (
                        <div style={{ marginTop: 10 }}>
                            <MCheckBox
                                label={t("Update Packages")}
                                checked={updatePackagesEnabled}
                                onChange={(e) => {
                                    const checked = !!e?.target?.checked;
                                    setUpdatePackagesEnabled(checked);
                                    if (checked) {
                                        initPackagesStateFromDTO(
                                            tender,
                                            packageTypes
                                        );
                                    }
                                }}
                            />
                        </div>
                    )}

                    {/* Packages panel */}
                    <div
                        className="divFormCreateTender"
                        style={{ marginTop: 10 }}
                    >
                        <PackagesPanel
                            containerTypeId={containerTypeId}
                            // FCL
                            equipments={equipments}
                            fclItems={fclItems}
                            setFclItems={setFclItems}
                            // LCL
                            lclMode={lclMode}
                            setLclMode={setLclMode}
                            lclBase={lclBase}
                            setLclBase={setLclBase}
                            packageTypes={packageTypes}
                            lclvolumes={lclVolumes}
                            setLclvolumes={setLclVolumes}
                            lclDimensions={lclDimensions}
                            setLclDimensions={setLclDimensions}
                            // UI
                            readOnly={packagesReadOnly}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

TenderConfigCrud.propTypes = {
    handleTabClose: PropTypes.func,
    toastMessages: PropTypes.func,
    isCarrierRealm: PropTypes.bool,
    onCreateTab: PropTypes.func,
    roles: PropTypes.array,
    // when provided, Bid does NOT open a new tab; I call this callback instead.
    // Signature: onOpenBid(bidContext, bidHeader)
    onOpenBid: PropTypes.func,
    // INVITE: optional deep-link context for carrier invite flows.
    inviteContext: PropTypes.shape({
        scope: PropTypes.oneOf(["tender", "phase"]),
        type: PropTypes.oneOf(["spot", "phase"]),
        tenderId: PropTypes.number,
        tenderPhaseId: PropTypes.number,
        token: PropTypes.string,
        createdAt: PropTypes.number,
    }),
};
